import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    DataServiceError,
    DefaultDataServiceConfig,
    EntitySelectors,
    EntitySelectorsFactory,
    HttpUrlGenerator
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap, withLatestFrom } from 'rxjs/operators';
import * as fromShared from '../../shared';
import {
    getDynamicColumns,
    Guid,
    IDeleteEntity,
    IGenericResponse,
    ResponseStatus
} from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { EfaactDynamicDataService } from '../efaact.dynamic-data-service';
import { ProjectBudget } from './project-budget.model';
export const entityName = 'ProjectBudget';

@Injectable()
export class ProjectBudgetDataService extends EfaactDynamicDataService<ProjectBudget> {
    projectBudgetSelectors: EntitySelectors<ProjectBudget>;
    filterItems = ['Status', 'AutoCalculateAmount', 'Row_Version', 'updates'];
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        log: LoggingService,
        config: DefaultDataServiceConfig,
        store: Store
    ) {
        super(http, httpUrlGenerator, entityName, log, config, store);
        this.projectBudgetSelectors =
            new EntitySelectorsFactory().create<ProjectBudget>(this.entityName);
        log.verbose({
            type: `${entityName}DataService`,
            msg: '::'
        });
    }

    public add(entity: ProjectBudget): Observable<ProjectBudget> {
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):add`,
            entity
        });
        return this.save({ id: Guid.newGuid(), changes: entity }).pipe(
            map(() => ({ ...entity }))
        );
    }
    protected save(update: Update<ProjectBudget>): Observable<ProjectBudget> {
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):save`,
            update
        });
        let newBudget = false;
        const item = {
            ...update.changes,
            JumpStart: (update.changes as any).JumpStart || false,
            updates: []
        } as any;
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):save-1`,
            item
        });
        const cols = getDynamicColumns(item, this.filterItems);
        cols.forEach(c => {
            const keys = Object.keys(item[c]);
            if (item[c].TxnID) {
                // have to remove the spaces from the property names
                const updateItem = {};
                keys.forEach(k => {
                    updateItem[k.replace(' ', '')] = item[c][k];
                });
                item.updates.push({
                    ...updateItem,
                    CostType: update.changes.CostType
                });
            }
            // keys.forEach(k => {
            //     item[`${c}_${k}`] = item[c][k];
            // });
            delete item[c];
        });
        if (item.updates && !item.updates.length) {
            delete item.updates;
        }
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):save-2`,
            item,
            cols
        });
        let req = {} as any;
        if (item.updates && item.updates.length) {
            req = item.updates;
            req[0] = {
                ...req[0],
                ...item
            };
            delete req[0].updates;
        } else {
            newBudget = true;
            req = [item];
        }
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):save-3`,
            req
        });
        return this.http
            .put<IGenericResponse<any>>(this.entitiesUrl, {
                [`${this.entityName}List`]: req
            })
            .pipe(
                map(resp => {
                    this.log.verbose({
                        type: `${this.entityName}DataService(ProjectBudgetDataService):save:resp`,
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    update.changes.original &&
                                                    update.changes.original.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );
                        throw new DataServiceError(
                            fromShared.Errors.getResponseErrors(resp).reduce(
                                (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                ''
                            ),
                            { method: 'PUT', url: this.entitiesUrl }
                        );
                    } else {
                        const ret = {
                            // ...update.changes,
                            Status: {
                                StatusCode: ResponseStatus.Success,
                                Message: 'Saved'
                            }
                        } as ProjectBudget;
                        if (!newBudget) {
                            cols.forEach(c => {
                                const resUpd = resp.Items.find(
                                    x => x.TxnID === update.changes[c].TxnID
                                );
                                const updCols = Object.keys(update.changes[c]);
                                updCols.forEach(u => {
                                    // ret[c][u] = resUpd[c][u];
                                    ret[c] = {
                                        ...(ret[c] || {}),
                                        [u]: resUpd[u.replace(' ', '')]
                                    };
                                });
                                delete ret[c].AutoCalculateAmount;
                            });
                            this.log.verbose({
                                type: `${this.entityName}DataService(ProjectBudgetDataService):save:return`,
                                ret,
                                cols,
                                update,
                                resp: resp.Items
                            });
                        } else {
                            cols.forEach(c => {
                                const resUpd = resp.Items.find(
                                    x => x.TxnID === update.changes[c].TxnID
                                );
                                ret[c] = {
                                    ...ret[c],
                                    ...(resUpd || {})
                                };
                                delete ret[c].AutoCalculateAmount;
                            });
                        }
                        return ret;
                    }
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }
    delete(key: number | string): Observable<number | string> {
        this.log.verbose({
            type: `${this.entityName}DataService(ProjectBudgetDataService):delete`,
            key
        });
        const params = {
            EntityIDs: [key]
        };
        return this.http
            .request<IGenericResponse<IDeleteEntity>>(
                'DELETE',
                this.entitiesUrl,
                {
                    body: params
                }
            )
            .pipe(
                tap(resp =>
                    this.log.verbose({
                        type: `${this.entityName}DataService(ProjectBudgetDataService):delete`,
                        resp
                    })
                ),
                withLatestFrom(
                    this.store.pipe(
                        select(this.projectBudgetSelectors.selectEntities)
                    )
                ),
                map(([resp, budgets]) => {
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus(resp)
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'DELETE', url: this.entitiesUrl }
                            )
                        );
                    } else {
                        const txnId = resp.Items[0].EntityID;
                        const cols = getDynamicColumns(
                            budgets[0],
                            this.filterItems
                        );
                        this.log.verbose({
                            type: `${this.entityName}DataService(ProjectBudgetDataService):delete`,
                            txnId,
                            cols,
                            budgets
                        });
                        return budgets.find(b =>
                            cols.some(c => b[c]?.TxnID === txnId)
                        );
                    }
                }),
                map(result =>
                    typeof result === 'object' && 'id' in result ? result.id : 0
                ), // Extract 'id' if result is an object
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }
}
